import { useQuery } from "@tanstack/react-query";
import { format } from "date-fns";
import {
  ArrowLeft,
  EllipsisVerticalIcon,
  ListIcon,
  TrashIcon,
} from "lucide-react";
import React from "react";
import { useParams } from "react-router-dom";

import { Badge, Button, Card, CardContent, PermissionGuard } from "@/components";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useI18n } from "@/context/i18n";
import { usePermissions } from "@/context/permissions";

import * as clientsApi from "./clients.api";
import { useClientPlans } from "./use-client-plans.hooks";

export const PlansPage: React.FC = () => {
  const { t } = useI18n();
  const { hasAnyPermission } = usePermissions();
  const { clientId } = useParams<{ clientId: string }>();

  // Verificar se o usuário tem pelo menos uma permissão para ações
  const hasActionPermissions = hasAnyPermission(["partners:edit", "partners:delete"]);

  const { data: client, isLoading: isLoadingClient } = useQuery({
    queryKey: ["client", clientId],
    queryFn: () => clientsApi.getClientById(clientId || ""),
    enabled: !!clientId,
  });

  const { 
    clientPlans, 
    isLoadingPlans, 
    error, 
    isDeletingPlan,
    handleBackToClients,
    handleEditPlan,
    handleDeletePlanWithConfirmation
  } = useClientPlans(clientId || "");


  if (isLoadingPlans || isLoadingClient) {
    return (
      <div className="flex flex-col w-full h-full space-y-6 p-4">
        <div className="flex items-center justify-center py-8">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-2"></div>
            <p className="text-muted-foreground">Carregando dados...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col w-full h-full space-y-6 p-4">
        <div className="flex items-center justify-center py-8">
          <div className="text-center">
            <p className="text-destructive mb-4">{error}</p>
            <Button type="button" variant="outline" onClick={handleBackToClients}>
              {t("generic.goBack")}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col w-full h-full space-y-6 p-4">
      {/* Header */}
      <div className="flex flex-row items-center justify-between">
        <div className="flex flex-col w-full px-2">
          <div className="flex items-center gap-3 mb-2">
            <Button
              type="button"
              variant="ghost"
              onClick={handleBackToClients}
              size="sm"
              className="p-3"
            >
              <ArrowLeft className="h-8 w-8" />
            </Button>
            <h1 className="text-3xl font-bold tracking-tight">
              {t("clients.subtitle.partnerPlans")}
            </h1>
          </div>
          {client && (
            <p className="text-muted-foreground">
             {t("clients.partner")}: <span className="font-medium">{client.name}</span>
            </p>
          )}
        </div>
      </div>

      {/* Conteúdo */}
      <Card className="p-4">
        <CardContent>
          {clientPlans.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full py-8">
              <ListIcon className="h-12 w-12 text-muted-foreground mb-4" />
              <p className="text-muted-foreground text-lg mb-2">
                Nenhum plano encontrado
              </p>
              <p className="text-muted-foreground text-sm">
                Este cliente ainda não possui planos cadastrados.
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-semibold">
                  Planos ({clientPlans.length})
                </h2>
              </div>

              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Nome</TableHead>
                    <TableHead>Preço</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Data de Criação</TableHead>
                    {hasActionPermissions && (
                      <TableHead>Ações</TableHead>
                    )}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {clientPlans.map((plan) => (
                    <TableRow key={plan.id}>
                      <TableCell className="font-medium">{plan.name}</TableCell>
                      <TableCell>
                        {new Intl.NumberFormat("pt-BR", {
                          style: "currency",
                          currency: "BRL",
                        }).format(plan.plan_price / 100)}
                      </TableCell>
                      <TableCell>
                        <Badge
                          className={
                            plan.is_active
                              ? "bg-green-500 text-white hover:bg-green-600"
                              : "bg-red-500 text-white hover:bg-red-600"
                          }
                        >
                          {plan.is_active ? "Ativo" : "Inativo"}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {format(new Date(plan.created_at), "dd/MM/yyyy")}
                      </TableCell>
                      {hasActionPermissions && (
                        <TableCell>
                          <PermissionGuard
                            permissions={["partners:edit", "partners:delete"]}
                            requireAll={false}
                          >
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button size={"sm"} variant={"ghost"} className="p-2">
                                  <EllipsisVerticalIcon className="h-4 w-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent className="p-auto">
                                <PermissionGuard permission="partners:edit">
                                  <DropdownMenuItem className=" w-full" onSelect={() => handleEditPlan(plan.id)}>
                                    {t("generic.edit")}
                                  </DropdownMenuItem>
                                </PermissionGuard>
                                <PermissionGuard permission="partners:delete">
                                  <DropdownMenuItem 
                                    className="bg-red-400 w-full hover:bg-red-600" 
                                    onSelect={() => handleDeletePlanWithConfirmation(plan.id, plan.name)}
                                    disabled={isDeletingPlan}
                                  >
                                    <TrashIcon className="h-4 w-4 mr-2" />
                                    {isDeletingPlan ? t("generic.deleting") : t("generic.delete")}
                                  </DropdownMenuItem>
                                </PermissionGuard>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </PermissionGuard>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
